import React, { useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIntl } from 'react-intl';
import './DatePickerPopup.scss';

export interface DayProgress {
  completed: number;
  total: number;
}

interface DatePickerPopupProps {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  onClose: () => void;
  excludeCloseRef?: React.RefObject<HTMLElement | null>;
  getDayProgress?: (day: Date) => DayProgress;
}

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// Returns Date objects for each day cell in the 6-week grid
function buildGridDays(year: number, month: number): Date[] {
  const firstOfMonth = new Date(year, month, 1);
  // 0=Sun → convert to Mon-based 0-index
  const startDow = (firstOfMonth.getDay() + 6) % 7; // 0=Mon
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(year, month, 1 - startDow + i));
  }
  return days;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const RING_RADIUS = 15;
const RING_PATH_LENGTH = 100;

const DatePickerPopup: React.FC<DatePickerPopupProps> = ({
  selectedDate,
  onSelect,
  onClose,
  excludeCloseRef,
  getDayProgress,
}) => {
  const { formatMessage: t } = useIntl();
  const [viewYear, setViewYear] = React.useState(selectedDate.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(selectedDate.getMonth());
  const ref = useRef<HTMLDivElement>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const gridDays = buildGridDays(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const handleDayClick = (day: Date) => {
    onSelect(new Date(day));
    onClose();
  };

  const toKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const selectedKey = toKey(selectedDate);
  const todayKey = toKey(today);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (excludeCloseRef?.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, excludeCloseRef]);

  return (
    <div className="dpp" ref={ref} role="dialog" aria-label={t({ id: 'daily.datePicker.label' })}>
      <div className="dpp__header">
        <button className="dpp__nav" onClick={prevMonth} type="button" aria-label={t({ id: 'daily.datePicker.prevMonth' })}>
          <ChevronLeft size={16} />
        </button>
        <span className="dpp__month-label">{MONTH_NAMES[viewMonth]} {viewYear}</span>
        <button className="dpp__nav" onClick={nextMonth} type="button" aria-label={t({ id: 'daily.datePicker.nextMonth' })}>
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="dpp__grid">
        {DAY_HEADERS.map(h => (
          <span key={h} className="dpp__dow">{h}</span>
        ))}
        {gridDays.map(day => {
          const key = toKey(day);
          const isCurrentMonth = day.getMonth() === viewMonth;
          const isSelected = key === selectedKey;
          const isToday = key === todayKey;
          const progress = getDayProgress?.(day);
          const total = progress?.total ?? 0;
          const completed = progress?.completed ?? 0;
          const pct = total > 0 ? Math.min(1, completed / total) : 0;
          const hasProgress = total > 0;
          const isComplete = hasProgress && completed >= total;
          const ringDash = isComplete ? `${RING_PATH_LENGTH} 0` : `${pct * RING_PATH_LENGTH} ${RING_PATH_LENGTH}`;
          return (
            <button
              key={key}
              type="button"
              className={[
                'dpp__day',
                !isCurrentMonth && 'dpp__day--other',
                isSelected && 'dpp__day--selected',
                isToday && !isSelected && 'dpp__day--today',
                hasProgress && 'dpp__day--progress',
                isComplete && 'dpp__day--complete',
              ].filter(Boolean).join(' ')}
              aria-pressed={isSelected}
              aria-label={
                hasProgress
                  ? t(
                      { id: 'daily.datePicker.dayProgress' },
                      { day: day.getDate(), completed, total },
                    )
                  : undefined
              }
              onClick={() => handleDayClick(day)}
            >
              {hasProgress && (
                <svg className="dpp__ring" viewBox="0 0 36 36" aria-hidden="true">
                  <circle className="dpp__ring-track" cx="18" cy="18" r={RING_RADIUS} pathLength={RING_PATH_LENGTH} />
                  {pct > 0 && (
                    <circle
                      className="dpp__ring-fill"
                      cx="18"
                      cy="18"
                      r={RING_RADIUS}
                      pathLength={RING_PATH_LENGTH}
                      strokeDasharray={ringDash}
                    />
                  )}
                </svg>
              )}
              <span className="dpp__day-num">{day.getDate()}</span>
            </button>
          );
        })}
      </div>

      <div className="dpp__footer">
        <button
          type="button"
          className="dpp__today-btn"
          onClick={() => { handleDayClick(new Date()); }}
        >
          {t({ id: 'daily.datePicker.today' })}
        </button>
      </div>
    </div>
  );
};

export default DatePickerPopup;
